import awsconfig from '@/amplifyconfiguration.json';
import { ErrorDialog } from '@/features/misc/ErrorDialog';
import { createPatient } from '@/graphql/mutations';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';

import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { DynamoExceptionClassName } from '../constants/dynamoExceptions';
import { useGetPatient } from '../hooks/useGetPatient';
import { PatientInformationType } from '../types/PatientInformationType';
import { CreatePatientMutation } from '@/API';
import { executeQuery } from '@/client/graphql';

Amplify.configure(awsconfig);

const schema: yup.ObjectSchema<PatientInformationType> = yup.object({
  familyName: yup.string().max(32, '姓を32文字以内で入力してください。').required('姓を入力してください。'),
  firstName: yup.string().max(32, '名を32文字以内で入力してください。').required('名を入力してください。'),
  familyNameKana: yup
    .string()
    .matches(/^[ァ-ヶー]{0,32}$/, 'セイ(カタカナ)は全角カタカナ32文字以内で入力してください。')
    .required('セイ(カタカナ)を入力してください。'),
  firstNameKana: yup
    .string()
    .matches(/^[ァ-ヶー]{0,32}$/, 'メイ(カタカナ)は全角カタカナ32文字以内で入力してください。')
    .required('メイ(カタカナ)を入力してください。'),
  email: yup
    .string()
    .required('メールアドレスを入力してください。')
    .email('メールアドレスを正しい形式で入力してください'),
  phoneNumber: yup
    .string()
    .matches(/^[0-9]{10,11}$/, '電話番号は半角数字11文字以内で入力してください。')
    .required('電話番号を入力してください。'),
  yearOfBirth: yup
    .string()
    .matches(/^(19|20)[0-9]{2}$/, '年を正しい形式で入力してください。')
    .required('年を入力してください。'),
  monthOfBirth: yup
    .string()
    .matches(/^(0?[1-9]|1[0-2])$/, '月を正しい形式で入力してください。')
    .required('月を入力してください。'),
  dateOfBirth: yup
    .string()
    .matches(/^(0?[1-9]|[12][0-9]|3[01])$/, '日を正しい形式で入力してください。')
    .required('日を入力してください。')
});

export function PatientInformationForm(props: any) {
  const methods = useForm<PatientInformationType>({
    defaultValues: {
      familyName: '',
      firstName: '',
      familyNameKana: '',
      firstNameKana: '',
      email: '',
      phoneNumber: '',
      yearOfBirth: '',
      monthOfBirth: '',
      dateOfBirth: ''
    },
    resolver: yupResolver(schema)
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    setValue
  } = methods;

  const { loading } = useGetPatient();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState<string>('');

  const [hasEmail, setHasEmail] = useState<boolean>(false);

  useEffect(() => {
    const setEmail = async () => {
      try {
        const attributes = await fetchUserAttributes();
        const email = attributes.email;
        if (email) {
          // email+passwordで登録されたユーザの分岐
          setValue('email', email);
          setHasEmail(true);
        }
      } catch (e) {
        // LINEユーザはemailが取得できないのが正常なので、エラー表示しない
        console.log('メールアドレスが取得できませんでした:', e);
      }
    };

    setEmail();
  }, []);

  async function onSubmit() {
    setConfirmDialogOpen(true);
  }

  const parseBirthFormToAWSDate = (year: string, month: string, date: string) => {
    return `${year}-${month}-${date}`;
  };

  const confirmBirthString = (year: string, month: string, date: string) => {
    return `${year} 年 ${parseInt(month)}月 ${parseInt(date)}日`;
  };

  function formatTwoDigits() {
    // 生年月日の月と日が1桁の場合左に0をつける
    // 例: 9　→ 09　に変換
    const formValues = getValues();

    // 月
    const month = formValues.monthOfBirth;
    if (month && month.length === 1) {
      const monthTwoDigits = `0${month}`;
      setValue('monthOfBirth', monthTwoDigits);
    }

    // 日
    const date = formValues.dateOfBirth;
    if (date && date.length === 1) {
      const dateTwoDigits = `0${date}`;
      setValue('dateOfBirth', dateTwoDigits);
    }
  }

  // お客様情報登録実行
  async function handleSavePatientInformation() {
    // 「9月9日」を「09月09日」にする処理
    formatTwoDigits();

    const formValues = getValues();
    try {
      setBackdropOpen(true);
      const authSession = await fetchAuthSession();
      const dateOfBirth = parseBirthFormToAWSDate(
        formValues.yearOfBirth,
        formValues.monthOfBirth,
        formValues.dateOfBirth
      );

      await executeQuery<CreatePatientMutation>(createPatient, {
        input: {
          familyName: formValues.familyName,
          firstName: formValues.firstName,
          familyNameKana: formValues.familyNameKana,
          firstNameKana: formValues.firstNameKana,
          email: formValues.email,
          phoneNumber: formValues.phoneNumber,
          dateOfBirth: dateOfBirth,
          id: authSession.userSub,
          facilityId: '',
          karteNo: '',
          vip: sessionStorage.getItem('mt') === '1',
          lineUserId: '',
          isLineNotificationEnabled: true
        }
      });

      if ('ReactNativeWebView' in window) {
        window.ReactNativeWebView?.postMessage(JSON.stringify({ userRegistration: true }));
      }

      setConfirmDialogOpen(false);
      if (props.needsRedirectCalendarPage) {
        // LINEからカレンダーへ遷移してきた場合はカレンダーページへ遷移する
        props.setActiveStep(2);
      } else {
        props.toTopPage != null ? props.toTopPage() : props.handleNext();
      }
    } catch (e: any) {
      console.log(e);
      if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
        Sentry.captureException(e);
      }

      if (e.errors) {
        switch (e.errors[0].errorType) {
          case DynamoExceptionClassName.duplicate:
            // ここで、サインイン後の登録ページに飛ばす
            // props.setActivePage(PageName.AlreadyRegistered);
            break;
          default:
            setErrorDialogMessage(
              `お客様情報の登録時にエラーが発生しました。\n（エラーコード：${e.errors[0].message}）`
            );
            break;
        }
      } else {
        setErrorDialogMessage(`お客様情報の登録時にエラーが発生しました。\n（エラーコード：${e.toString()}）`);
      }
      setErrorDialogOpen(true);
      setBackdropOpen(false);
      setConfirmDialogOpen(false);
    }
  }

  const ErrorMessage = (props: { message: string | undefined }) => {
    if (!props.message) return null;
    return (
      <Typography color="error" style={{ fontSize: '12px', marginLeft: '4px' }}>
        {props.message}
      </Typography>
    );
  };

  const BackDrop = (props: { isOpen: boolean }) => {
    return (
      <Backdrop
        sx={{ color: '#fff', backgroundColor: '#0003', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={props.isOpen}
      >
        <CircularProgress color={'inherit'} />
      </Backdrop>
    );
  };

  const ConfirmDialog = () => {
    const ConfirmInformations = () => {
      const formValues = getValues();
      return (
        <Box>
          <Grid sx={{ textAlign: 'left' }} p={'0 24px'}>
            <Grid py={1} mx={0.5}>
              <Typography fontSize={18} color={'#000 !important'}>
                氏名
              </Typography>
              <Typography fontSize={16}>
                {`${formValues.familyName}　${formValues.firstName}（${formValues.familyNameKana}　${formValues.firstNameKana}）`}
              </Typography>
            </Grid>
            <Grid py={1} mx={0.5}>
              <Typography fontSize={18} color={'#000 !important'}>
                生年月日
              </Typography>
              <Typography fontSize={16}>
                {confirmBirthString(formValues.yearOfBirth, formValues.monthOfBirth, formValues.dateOfBirth)}
              </Typography>
            </Grid>
            <Grid py={1} mx={0.5}>
              <Typography fontSize={18} color={'#000 !important'}>
                メールアドレス
              </Typography>
              <Typography fontSize={16}>{formValues.email}</Typography>
            </Grid>
            <Grid py={1} mx={0.5}>
              <Typography fontSize={18} color={'#000 !important'}>
                電話番号
              </Typography>
              <Typography fontSize={16}>{formValues.phoneNumber}</Typography>
            </Grid>
          </Grid>
        </Box>
      );
    };

    const ConfirmButton = () => {
      return (
        <button
          type="button"
          style={{ width: '240px', height: '40px', color: '#FFFFFF', textAlign: 'center', margin: 'auto' }}
          className="bg-primary flex relative rounded"
          disabled={!!!confirmDialogOpen}
          onClick={handleSavePatientInformation}
          autoFocus
        >
          <div className="text-md m-auto">{'上記の内容で確定する'}</div>
        </button>
      );
    };

    return (
      <Dialog
        open={confirmDialogOpen}
        fullWidth
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <IconButton
          aria-label="close"
          onClick={() => {
            setConfirmDialogOpen(false);
          }}
          sx={(theme) => ({
            position: 'relative',
            padding: 0,
            marginLeft: 'auto',
            paddingRight: '8px',
            paddingTop: '8px',
            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ textAlign: 'center', backgroundColor: '#FFF', paddingBottom: '32px' }}>
          <DialogTitle
            fontSize={18}
            className="font-bold"
            id="alert-dialog-title"
            data-gtm-dialog-title={'登録内容確認'}
            sx={{ padding: 0, marginBottom: '16px' }}
          >
            登録内容確認
          </DialogTitle>
          <DialogContentText id="alert-dialog-description" sx={{ marginBottom: '16px' }}>
            <ConfirmInformations />
          </DialogContentText>
          <DialogActions>
            <ConfirmButton />
          </DialogActions>
        </DialogContent>
      </Dialog>
    );
  };

  const SubmitButton = () => {
    return (
      <button
        type="submit"
        style={{ width: '100%', height: '48px', color: '#FFFFFF', textAlign: 'center', margin: 'auto' }}
        className="bg-primary flex relative rounded"
      >
        <div className="text-md m-auto">{'登録内容を確認する'}</div>
      </button>
    );
  };

  // 患者情報の登録済チェック完了まではローディングのみ表示
  if (loading) {
    return <BackDrop isOpen={loading} />;
  }

  return (
    <FormProvider {...methods}>
      <Box sx={{ p: 2 }}>
        <Grid container direction={'row'} justifyContent={'center'} spacing={2}>
          <Grid item xs={12} sm={8}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container direction={'column'} spacing={2}>
                <Grid item xs={12}>
                  <Typography fontSize={14} mb={0.5}>
                    以下のお客様情報をご入力お願いいたします。
                  </Typography>
                </Grid>
                {/* 名前入力フィールド */}
                <Grid item xs={12}>
                  <Grid container direction="row" justifyContent={'space-around'} spacing={2}>
                    <Grid item xs={6} sm={12}>
                      <Stack direction={'column'} spacing={1}>
                        <InputLabel className="font-bold" error={!!errors.familyName}>
                          <Box fontSize={14} ml={0.5}>
                            姓
                          </Box>
                        </InputLabel>
                        <Controller
                          name="familyName"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <TextField
                                {...field}
                                type="text"
                                placeholder="港"
                                fullWidth
                                size="small"
                                sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                                error={!!error}
                              />
                              {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                            </>
                          )}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={6} sm={12}>
                      <Stack direction={'column'} spacing={1}>
                        <InputLabel className="font-bold" error={!!errors.firstName}>
                          <Box fontSize={14} ml={0.5}>
                            名
                          </Box>
                        </InputLabel>
                        <Controller
                          name="firstName"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <TextField
                                {...field}
                                type="text"
                                placeholder="太郎"
                                fullWidth
                                size="small"
                                sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                                error={!!error}
                              />
                              {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                            </>
                          )}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
                {/* カタカナ入力フィールド */}
                <Grid item xs={12}>
                  <Grid container direction="row" justifyContent={'space-around'} spacing={2}>
                    <Grid item xs={6} sm={12}>
                      <Stack direction={'column'} spacing={1}>
                        <InputLabel className="font-bold" error={!!errors.familyNameKana}>
                          <Box fontSize={14} ml={0.5}>
                            姓(カタカナ)
                          </Box>
                        </InputLabel>
                        <Controller
                          name="familyNameKana"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <TextField
                                {...field}
                                type="text"
                                placeholder="ミナト"
                                fullWidth
                                size="small"
                                sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                                error={!!error}
                              />
                              {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                            </>
                          )}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={6} sm={12}>
                      <Stack direction={'column'} spacing={1}>
                        <InputLabel className="font-bold" error={!!errors.firstNameKana}>
                          <Box fontSize={14} ml={0.5}>
                            名(カタカナ)
                          </Box>
                        </InputLabel>
                        <Controller
                          name="firstNameKana"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <TextField
                                {...field}
                                type="text"
                                placeholder="タロウ"
                                fullWidth
                                size="small"
                                sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                                error={!!error}
                              />
                              {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                            </>
                          )}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
                {/* 生年月日入力フィールド */}
                <Grid item xs={12}>
                  <Stack direction={'column'}>
                    <InputLabel className="font-bold">
                      <Box fontSize={14} ml={0.5} color={'#424242'}>
                        生年月日
                      </Box>
                    </InputLabel>
                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                      <Controller
                        name="yearOfBirth"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <Grid sx={{ display: 'flex', flexDirection: 'column' }}>
                            <TextField
                              {...field}
                              type="text"
                              placeholder="1990"
                              fullWidth
                              size="small"
                              sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                              error={!!error}
                            />
                            {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                          </Grid>
                        )}
                      />
                      <Typography fontSize={14} ml={0.5} className="font-bold" color={'inherit'}>
                        年
                      </Typography>
                      <Controller
                        name="monthOfBirth"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <Grid sx={{ display: 'flex', flexDirection: 'column' }}>
                            <TextField
                              {...field}
                              type="text"
                              placeholder="01"
                              fullWidth
                              size="small"
                              sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                              error={!!error}
                            />
                            {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                          </Grid>
                        )}
                      />
                      <Typography fontSize={14} ml={0.5} className="font-bold" color={'inherit'}>
                        月
                      </Typography>
                      <Controller
                        name="dateOfBirth"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <Grid sx={{ display: 'flex', flexDirection: 'column' }}>
                            <TextField
                              {...field}
                              type="text"
                              placeholder="01"
                              fullWidth
                              size="small"
                              sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                              error={!!error}
                            />
                            {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                          </Grid>
                        )}
                      />
                      <Typography fontSize={14} ml={0.5} className="font-bold" color={'inherit'}>
                        日
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
                {/* メールアドレス */}
                {!hasEmail ? (
                  <Grid item xs={12}>
                    <Stack direction={'column'}>
                      <InputLabel className="font-bold">
                        <Box fontSize={14} ml={0.5} color={'#424242'}>
                          メールアドレス
                        </Box>
                      </InputLabel>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <TextField
                              {...field}
                              type="text"
                              placeholder="example@healthinterview.clinic"
                              fullWidth
                              size="small"
                              sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                              error={!!error}
                            />
                            {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                          </>
                        )}
                      />
                    </Stack>
                  </Grid>
                ) : null}
                {/* 電話番号 */}
                <Grid item xs={12}>
                  <Stack direction={'column'}>
                    <InputLabel className="font-bold">
                      <Box fontSize={14} ml={0.5} color={'#424242'}>
                        電話番号
                      </Box>
                    </InputLabel>
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <TextField
                            {...field}
                            type="text"
                            placeholder="09012345678"
                            fullWidth
                            size="small"
                            sx={{ backgroundColor: '#fff', marginTop: '4px !important' }}
                            error={!!error}
                          />
                          {!!error && <ErrorMessage message={errors[field.name]?.message} />}
                        </>
                      )}
                    />
                  </Stack>
                </Grid>

                {/* 送信ボタン */}
                <Grid item sx={{ mt: 2 }}>
                  <SubmitButton />
                </Grid>
              </Grid>
            </form>
            {/* LINE連携は仕様からなくなったため、コメントアウト */}
            {/* <Grid item sx={{ mt: 2 }}>
              <LinkLineButton />
            </Grid> */}
          </Grid>

          <ConfirmDialog />

          <ErrorDialog
            open={errorDialogOpen}
            title={'お客様情報登録エラー'}
            buttonLabel="閉じる"
            buttonAction={() => {
              setErrorDialogOpen(false);
            }}
          >
            {errorDialogMessage}
          </ErrorDialog>
        </Grid>
        <BackDrop isOpen={backdropOpen} />
      </Box>
    </FormProvider>
  );
}

// 428-塩竹: LINE連携が仕様から消されたが、将来的にLINE連携が必要になる可能性があるため、
// コメントアウトして残している;
// const LinkLineButton = () => {
//   const [isLinkedLine, setIsLinkedLine] = useState<boolean>(false);
//   const hasCheckedRef = useRef(false);

//   const LIFF_ID_PRD = '2005329688-NkAamy6R'; // 仮のIDなので、LINE側でエラーになるはず
//   // const LIFF_ID_DEV = '2005329688-3kO8pBoj';
//   const LIFF_ID_DEV = '2005329688-NkAamy6R';
//   const LIFF_ID = import.meta.env.VITE_USER_BRANCH === 'product' ? LIFF_ID_PRD : LIFF_ID_DEV;

//   const location = useLocation();
//   // ログイン状態でない場合ログインを促す
//   const handleLineLogin = async () => {
//     try {
//       await liffClient.initLiff({ liffId: LIFF_ID });
//       await liffClient.ready;
//       await liffClient.getProfile(
//         (profile) => {
//           // ここで画面遷移するため、以下の処理はuseEffectで次のマウント時に行う
//           const lineUserId = profile.userId;
//           linkUserToLine(lineUserId);
//           setIsLinkedLine(true);
//         },
//         () => {
//           console.log('プロフィール取得失敗');
//         }
//       );
//     } catch (error) {
//       console.log('LINEログインに失敗しました');
//       console.log('エラー:', error);
//     }
//   };

//   // ログイン状態であれば、LineのuserIdとCognitoユーザの紐付けを行う
//   // ログイン状態でなければ、何もしない
//   const linkUserIfLoggedIn = async () => {
//     try {
//       await liffClient.initLiff({ liffId: LIFF_ID });
//       liff.ready.then(() => {
//         if (!liff.isLoggedIn()) {
//           // ログインしていない場合は、ログイン画面に遷移するため
//           // 以降の処理を中止する
//           return;
//         }

//         liffClient.getProfile(
//           (profile) => {
//             const lineUserId = profile.userId;
//             console.log('LINE User ID:', lineUserId);
//             linkUserToLine(lineUserId);
//             setIsLinkedLine(true);
//           },
//           () => {
//             console.log('プロフィール取得失敗:');
//             setIsLinkedLine(false);
//           }
//         );
//       });
//     } catch (error) {
//       console.error('Error initializing LIFF:', error);
//     }
//   };

//   const linkUserToLine = async (lineUserId: string) => {
//     // どのusernameと紐付けるかを確認するため
//     // Cognitoオーソライザーのためにトークンを取得
//     const session = await fetchAuthSession();
//     const token = session?.tokens?.idToken?.toString();

//     try {
//       const response = await post({
//         apiName: 'lineAuth',
//         path: '/linkUserToLine',
//         options: {
//           headers: {
//             'Content-Type': 'application/json',
//             Authorization: String(token)
//           },
//           body: {
//             lineUserId: lineUserId
//           }
//         }
//       });
//       return response;
//     } catch (error) {
//       console.log('LINE連携に失敗しました', error);
//       return error;
//     }
//   };

//   useEffect(() => {
//     // LINEログインのリダイレクト時に連続でAPIを叩かれないようにフラグを設定
//     if (hasCheckedRef.current) return;
//     hasCheckedRef.current = true;

//     const checkAndLinkUser = async () => {
//       try {
//         // Cognitoユーザのidentitiesから、LINE連携済みかどうかを判断する
//         const attributes = await fetchUserAttributes();
//         const identities = attributes.identities ?? [];

//         if (identities.length > 0) {
//           setIsLinkedLine(true);
//           return;
//         }

//         await linkUserIfLoggedIn();
//       } catch (error) {
//         console.error('エラーが発生しました:', error);
//       }
//     };

//     checkAndLinkUser();
//   }, []);

//   return (
//     location.pathname === '/' && (
//       <Grid item>
//         <Stack direction={'column'}>
//           <InputLabel className="font-bold">
//             <Typography fontSize={14}>LINE連携をおこなう</Typography>
//           </InputLabel>

//           {isLinkedLine ? (
//             // 連携済み
//             <button
//               style={{
//                 width: '100%',
//                 height: '48px',
//                 backgroundColor: '#D3D3D3',
//                 color: '#A9A9A9',
//                 cursor: 'not-allowed',
//                 textAlign: 'center',
//                 margin: 'auto'
//               }}
//               className="flex relative rounded"
//               disabled
//             >
//               <div className="text-md m-auto">{'LINE連携済み'}</div>
//             </button>
//           ) : (
//             // 未連携の場合
//             <button
//               style={{
//                 width: '100%',
//                 height: '48px',
//                 color: '#FFFFFF',
//                 textAlign: 'center',
//                 margin: 'auto',
//                 backgroundColor: ColorPalettes.LINE_MAIN
//               }}
//               className="flex relative rounded"
//               onClick={() => handleLineLogin()}
//             >
//               <div className="text-md m-auto">{'LINE連携する'}</div>
//             </button>
//           )}
//         </Stack>
//       </Grid>
//     )
//   );
// };
